import {Component, Vue} from 'vue-property-decorator';
import axios from 'axios';
import {UserRoleEnum} from '@/enums/userRolesEnum';
import {getBadgesCount} from '@/badge/BadgeService';
import {BadgesCount} from '@/badge/BadgeModel';
import {BadgeEventBus} from '@/badge/BadgeEventBus';
import {links, SidebarLink} from '@/views/applicationShell/sidebar-links';

const ONE_MINUTE = 1000 * 60;
//default to 30 seconds
const BADGE_UPDATE_TIMER = parseInt(process.env.BADGE_UPDATE_TIMER || '30000', 10);
const hideIndicator = -54;
const selectedNavOffset = 123;
const selectedSubNavOffset = 10;

@Component
export default class ApplicationShell extends Vue {
    private selectedShadowTop = 69;
    private selectedSubMenuShadowTop = 10;
    private selectedRootPath = 'calendar-shifts';
    private selectedSubPath = '';
    private selectedRootMenuOption = '';
    private showSideNav = false;
    private expiredTokenInterval = null as any;
    private badgeUpdateInterval = null as any;
    private badge: BadgesCount = {communityPendingEventsCount: 0, pendingDvdOrders: 0, pendingSupportFormsCount: 0, pendingFormSubmissionCounts: 0};

    private cancelSource = axios.CancelToken.source();

    get communityCalendarPendingEvents(): number {
        return this.badge.communityPendingEventsCount || 0;
    }

    get pendingDvdOrders(): number {
        return this.badge.pendingDvdOrders || 0;
    }

    get pendingSupportFormCounts(): number {
        return this.badge.pendingSupportFormsCount || 0;
    }

    get pendingFormSubmissionCounts(): number {
        return this.badge.pendingFormSubmissionCounts || 0;
    }

    get pendingSupportFormAndCommunityCalendar(): number {
        return +this.pendingSupportFormCounts + +this.communityCalendarPendingEvents + +this.pendingFormSubmissionCounts;
    }

    get links(): SidebarLink[] {
        return links.filter(link => this.hasRole(link.roles) && !link.hide)
        .map(link => {
            if (link.subLinks && link.subLinks.length) {
                const subLinks = link.subLinks.filter(subLink => this.hasRole(subLink.roles) && !subLink.hide);
                if (subLinks.length) {
                    return {
                        ...link,
                        subLinks
                    };
                }
            }
            return link;
        });
    }

    private hasRole(userRoleEnum: Array<UserRoleEnum>) {
        return this.$store.getters['user/hasRole'](userRoleEnum);
    }

    created() {
        // If they don't have a token, log them out
        if (!this.$store.state.user.loggedInUser.token) {
            this.$notifySuccess('Please log in');
            this.$router.replace('/login');
            return;
        }

        // If their token is expired, log them out
        if (this.$store.getters['user/isTokenExpired']()) {
            this.$notifySuccess('Your Session Has Expired');
            this.$router.replace('/login');
            return;
        }

        this.expiredTokenInterval = setInterval(() => {
            if (this.$store.getters['user/isTokenExpired']()) {
                this.$notifySuccess('Your Session Has Expired');
                this.$router.replace('/login');
            }
        }, ONE_MINUTE);

        this.getBadgesCount();

        this.sidebarCountsIntervalAndEvent();
    }

    sidebarCountsIntervalAndEvent() {
        this.badgeUpdateInterval = setInterval(this.getBadgesCount, BADGE_UPDATE_TIMER);

        // Listen for the sidebar count update event.
        BadgeEventBus.$on('sidebar-counts-update', this.getBadgesCount);
    }

    private async getBadgesCount() {
        // check server directly for any badge update, we don't need to add badge to the store
        this.badge = await getBadgesCount(this.cancelSource.token);
    }

    beforeDestroy() {
        clearInterval(this.expiredTokenInterval);
        clearInterval(this.badgeUpdateInterval);
        BadgeEventBus.$off('sidebar-counts-update');
    }

    mounted() {
        // Bold the selected sidebar immediately so there's no delay.
        this.checkRoute();
    }

    updated() {
        this.checkRoute();
    }

    private checkRoute() {
        const path = this.$route.path;
        const selectedPath = path.split('/');
        const rootPath = selectedPath[1];
        const subPath = selectedPath[2];
        // Bolds the selected item in the left nav
        if (this.selectedRootMenuOption) {
            this.selectedRootPath = this.selectedRootMenuOption;
            // this is needed in order to retain the vertical red bar when they click to a different menu option,
            // but come back to the original one they were on
            this.selectedSubPath = rootPath === this.selectedRootPath ? subPath : '';
            // hide submenu selection here if route is changing, waiting for this.updateSidebarMarker() to do it makes the nav look gross
            this.selectedSubMenuShadowTop = this.selectedSubPath ? this.selectedSubMenuShadowTop : hideIndicator;
        } else {
            this.selectedRootPath = rootPath;
            this.selectedSubPath = subPath;
        }
        // Need to make sure we give any animations time to finish (e.g. collapsing the current section and expanding the new section)
        // Otherwise it bounces
        // The amount of the timeout was determined using trial and error until it didn't completely suck :(
        setTimeout(() => {
            this.updateSidebarMarker();
        }, 200);

        if (path === '/login') {
            this.$router.replace(path);
        } else {
            this.$router.push(path);
        }
    }

    private updateSidebarMarker() {
        // Place a vertical, red bar to the left of the selected item in the left nav
        const selectedNavElement = this.selectedRootPath ? document.getElementById(this.selectedRootPath) : null;
        const shadowBoxTop = selectedNavElement !== null ? selectedNavElement.offsetTop + selectedNavOffset : selectedNavOffset;
        const selectedSubNavElement = this.selectedSubPath ? document.getElementById(this.selectedSubPath) : null;
        this.selectedShadowTop = this.selectedRootPath ? shadowBoxTop : hideIndicator;
        if (selectedSubNavElement) {
            // Do the same for the sub-menu
            this.selectedSubMenuShadowTop = selectedSubNavElement.offsetTop + selectedSubNavOffset;
        } else {
            this.selectedSubMenuShadowTop = hideIndicator;
        }
    }

    private toggleSideNav() {
        this.showSideNav = !this.showSideNav;
    }

    private hideSideNav() {
        this.showSideNav = false;
        this.selectedRootMenuOption = '';
    }

    private storeSelectedRootMenuOption(link: SidebarLink) {
        this.selectedRootMenuOption = link.path;
    }

}
